import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { User } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Settings,
  Award,
  LogOut,
  Menu,
  Users,
  MessageSquare,
  BookOpen,
  Video,
  Rocket,
  LayoutDashboard,
  GraduationCap,
  ChevronDown,
  Compass,
  Shield,
  BookMarked,
  Lightbulb,
  BadgeCheck,
  Building2,
} from "lucide-react";
import maipLogo from "@/assets/maip-logo.png";
import { BedarfsAnalysatorDialog } from "@/components/BedarfsAnalysatorDialog";

// =============================================================================
// MEGA-MENU ITEM COMPONENT
// =============================================================================
interface MenuItemProps {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  badgeColor?: string;
  onClick?: () => void;
}

const MegaMenuItem: React.FC<MenuItemProps> = ({
  title,
  description,
  href,
  icon,
  badge,
  badgeColor = "bg-primary",
  onClick,
}) => (
  <Link
    to={href}
    onClick={onClick}
    className="group flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
  >
    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-foreground group-hover:text-primary transition-colors">
          {title}
        </span>
        {badge && (
          <span
            className={`px-1.5 py-0.5 text-[10px] font-bold ${badgeColor} text-primary-foreground rounded-full`}
          >
            {badge}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
        {description}
      </p>
    </div>
  </Link>
);

// =============================================================================
// MAIN HEADER COMPONENT
// =============================================================================
const Header = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isExpert, setIsExpert] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBedarfsAnalysator, setShowBedarfsAnalysator] = useState(false);
  const [academyMenuOpen, setAcademyMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkExpertRole(session.user.id);
        checkAdminRole(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkExpertRole(session.user.id);
        checkAdminRole(session.user.id);
      } else {
        setIsExpert(false);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkExpertRole = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .in("role", ["experte", "admin"]);

      setIsExpert(roles && roles.length > 0);
    } catch (error) {
      console.error("Error checking expert role:", error);
      setIsExpert(false);
    }
  };

  const checkAdminRole = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin");

      setIsAdmin(roles && roles.length > 0);
    } catch (error) {
      console.error("Error checking admin role:", error);
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const closeAcademyMenu = () => setAcademyMenuOpen(false);

  return (
    <header className="bg-background shadow-sm sticky top-0 z-50 border-b">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src={maipLogo} alt="MAIP.info Logo" className="h-[40px]" />
          </Link>

          {/* ============================================================= */}
          {/* DESKTOP NAVIGATION - 4 Main Entry Points                      */}
          {/* ============================================================= */}
          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {/* 1. Über MAIP */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium">
                  Über MAIP
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background z-50">
                <DropdownMenuItem asChild>
                  <Link to="/warum-maip" className="cursor-pointer">
                    Warum MAIP?
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/experten" className="cursor-pointer">
                    Dozierende & Team
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/gestaltungsrat" className="cursor-pointer">
                    Gestaltungsrat
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/faq" className="cursor-pointer">
                    FAQ
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/kontakt" className="cursor-pointer">
                    Kontakt
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 2. Akademie - MEGA MENU */}
            <Popover open={academyMenuOpen} onOpenChange={setAcademyMenuOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1.5 px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium">
                  <GraduationCap className="w-4 h-4" />
                  Akademie
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[800px] p-0 bg-background border shadow-xl rounded-xl"
                align="center"
                sideOffset={8}
              >
                {/* Mega Menu Content */}
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-6">
                    {/* Column 1: Zertifizierungs-Tracks */}
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-3 mb-3">
                        Karriere-Pfade
                      </h4>

                      <MegaMenuItem
                        title="KI-Pilot"
                        description="Operative Exzellenz in 7 Tagen."
                        href="/ki-pilot"
                        icon={<Rocket className="w-5 h-5 text-blue-500" />}
                        badge="🥈"
                        badgeColor="bg-slate-400"
                        onClick={closeAcademyMenu}
                      />

                      <MegaMenuItem
                        title="KI-Lotse"
                        description="Multiplikatoren-Ausbildung (14 Tage)."
                        href="/ki-lotse"
                        icon={<Compass className="w-5 h-5 text-amber-500" />}
                        badge="🥇"
                        badgeColor="bg-amber-500"
                        onClick={closeAcademyMenu}
                      />

                      <MegaMenuItem
                        title="MAV-KI-Lotse"
                        description="Rechtssichere Mitbestimmung."
                        href="/ki-lotse"
                        icon={<Shield className="w-5 h-5 text-emerald-500" />}
                        badge="Smaragd"
                        badgeColor="bg-emerald-500"
                        onClick={closeAcademyMenu}
                      />

                      <div className="pt-2 px-3">
                        <Link
                          to="/lotsen"
                          className="text-sm text-primary hover:underline font-medium"
                          onClick={closeAcademyMenu}
                        >
                          → Alle Tracks vergleichen
                        </Link>
                      </div>
                    </div>

                    {/* Column 2: Knowledge Hub */}
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-3 mb-3">
                        Knowledge Hub
                      </h4>

                      <MegaMenuItem
                        title="Programm-Übersicht"
                        description="Das vollständige MAIP-Curriculum."
                        href="/programm"
                        icon={<BookMarked className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />

                      <MegaMenuItem
                        title="Wissens-Hub"
                        description="Fachartikel zu Datenschutz & KI."
                        href="/wissen"
                        icon={<BookOpen className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />

                      <MegaMenuItem
                        title="KI-Glossar"
                        description="Alle Fachbegriffe erklärt."
                        href="/glossar"
                        icon={<BookMarked className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />

                      <MegaMenuItem
                        title="Roadmap 2026"
                        description="Kommende Features."
                        href="/#pipeline"
                        icon={<Lightbulb className="w-5 h-5 text-primary" />}
                        badge="Preview"
                        badgeColor="bg-muted-foreground"
                        onClick={closeAcademyMenu}
                      />
                    </div>

                    {/* Column 3: Governance */}
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-3 mb-3">
                        Governance
                      </h4>

                      <MegaMenuItem
                        title="Zertifikat prüfen"
                        description="Öffentliche Echtheitsprüfung."
                        href="/verify"
                        icon={<BadgeCheck className="w-5 h-5 text-green-600" />}
                        onClick={closeAcademyMenu}
                      />

                      {isAdmin && (
                        <MegaMenuItem
                          title="Master-Audit"
                          description="Verbundweite Steuerung."
                          href="/master-audit"
                          icon={<Building2 className="w-5 h-5 text-indigo-500" />}
                          badge="Träger"
                          badgeColor="bg-indigo-500"
                          onClick={closeAcademyMenu}
                        />
                      )}

                      <MegaMenuItem
                        title="Mein Cockpit"
                        description="Ihr persönliches Dashboard."
                        href="/cockpit"
                        icon={<LayoutDashboard className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />

                      {/* Quick Links */}
                      <div className="pt-4 px-3 border-t mt-4">
                        <p className="text-xs text-muted-foreground mb-2">
                          Module:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to="/grundlagen"
                            className="text-xs px-2 py-1 bg-muted rounded-md hover:bg-muted/80 transition-colors"
                            onClick={closeAcademyMenu}
                          >
                            KI-Grundlagen
                          </Link>
                          <Link
                            to="/datenschutz-kompass"
                            className="text-xs px-2 py-1 bg-muted rounded-md hover:bg-muted/80 transition-colors"
                            onClick={closeAcademyMenu}
                          >
                            Datenschutz
                          </Link>
                          <Link
                            to="/mitbestimmung-info"
                            className="text-xs px-2 py-1 bg-muted rounded-md hover:bg-muted/80 transition-colors"
                            onClick={closeAcademyMenu}
                          >
                            Mitbestimmung
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom CTA */}
                  <div className="mt-6 pt-4 border-t flex items-center justify-between">
                    <button
                      onClick={() => {
                        setShowBedarfsAnalysator(true);
                        closeAcademyMenu();
                      }}
                      className="text-sm font-semibold text-primary hover:underline"
                    >
                      🎯 Welches Modul passt zu mir?
                    </button>
                    <Link
                      to="/preise"
                      className="text-sm font-medium text-muted-foreground hover:text-foreground"
                      onClick={closeAcademyMenu}
                    >
                      Preise & Förderung →
                    </Link>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* 3. Mein Cockpit - Direct Link */}
            <Link
              to="/cockpit"
              className="flex items-center gap-1.5 px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium"
            >
              <LayoutDashboard className="w-4 h-4" />
              Mein Cockpit
            </Link>

            {/* 4. Preise */}
            <Link
              to="/preise"
              className="px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium"
            >
              Preise
            </Link>

            {/* Community - Only for Experts */}
            {isExpert && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium">
                    <Users className="w-4 h-4" />
                    Community
                    <ChevronDown className="w-4 h-4 opacity-50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link
                      to="/experten-bereich/forum"
                      className="cursor-pointer flex items-center"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Forum
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/experten-bereich/ressourcen"
                      className="cursor-pointer flex items-center"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Ressourcen
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/experten-bereich/webinare"
                      className="cursor-pointer flex items-center"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Webinare
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link
                      to="/experten-bereich"
                      className="cursor-pointer flex items-center"
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          {/* ============================================================= */}
          {/* DESKTOP: Admin Settings                                       */}
          {/* ============================================================= */}
          <div className="hidden lg:flex items-center gap-2">
            {isAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin-Bereich
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/experten-bereich"
                      className="cursor-pointer flex items-center"
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin/content"
                      className="cursor-pointer flex items-center"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Inhalte verwalten
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : user && isExpert ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link
                      to="/experten-bereich"
                      className="cursor-pointer flex items-center"
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>

          {/* ============================================================= */}
          {/* MOBILE: Hamburger Menu                                        */}
          {/* ============================================================= */}
          <div className="flex lg:hidden items-center gap-3">
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin-Bereich
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/experten-bereich"
                      className="cursor-pointer flex items-center"
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-primary" />
                    Navigation
                  </SheetTitle>
                </SheetHeader>

                <nav className="flex flex-col gap-2 mt-6">
                  {/* Mein Cockpit - Prominent */}
                  <Link
                    to="/cockpit"
                    className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg text-foreground hover:bg-primary/10 transition-colors font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-5 h-5 text-primary" />
                    Mein Cockpit
                  </Link>

                  {/* Karriere-Pfade */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                    Karriere-Pfade
                  </div>
                  <Link
                    to="/ki-pilot"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Rocket className="w-5 h-5 text-blue-500" />
                    <div>
                      <span className="font-medium">KI-Pilot</span>
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-slate-400 text-white rounded-full">
                        🥈
                      </span>
                    </div>
                  </Link>
                  <Link
                    to="/ki-lotse"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Compass className="w-5 h-5 text-amber-500" />
                    <div>
                      <span className="font-medium">KI-Lotse</span>
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                        🥇
                      </span>
                    </div>
                  </Link>

                  {/* Knowledge Hub */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                    Knowledge Hub
                  </div>
                  <Link
                    to="/programm"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Programm-Übersicht
                  </Link>
                  <Link
                    to="/wissen"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Wissens-Hub
                  </Link>
                  <Link
                    to="/glossar"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    KI-Glossar
                  </Link>
                  <Link
                    to="/grundlagen"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    KI-Grundlagen
                  </Link>
                  <Link
                    to="/datenschutz-kompass"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Datenschutz-Kompass
                  </Link>
                  <Link
                    to="/mitbestimmung-info"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    KI & Mitbestimmung
                  </Link>

                  {/* Über MAIP */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                    Über MAIP
                  </div>
                  <Link
                    to="/warum-maip"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Warum MAIP?
                  </Link>
                  <Link
                    to="/experten"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dozierende & Team
                  </Link>
                  <Link
                    to="/gestaltungsrat"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Gestaltungsrat
                  </Link>

                  {/* Service */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                    Service
                  </div>
                  <Link
                    to="/preise"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3 font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Preise & Förderung
                  </Link>
                  <Link
                    to="/faq"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    FAQ
                  </Link>
                  <Link
                    to="/kontakt"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Kontakt
                  </Link>
                  <Link
                    to="/verify"
                    className="text-foreground hover:text-primary transition-colors py-2 pl-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Zertifikat prüfen
                  </Link>

                  {/* Bedarfs-Analysator CTA */}
                  <button
                    onClick={() => {
                      setShowBedarfsAnalysator(true);
                      setMobileMenuOpen(false);
                    }}
                    className="mt-4 w-full p-3 bg-primary text-primary-foreground rounded-lg font-semibold text-center"
                  >
                    🎯 Welches Modul passt zu mir?
                  </button>

                  {/* Community - Only for Experts */}
                  {isExpert && (
                    <>
                      <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                        Community
                      </div>
                      <Link
                        to="/experten-bereich/forum"
                        className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <MessageSquare className="w-4 h-4" />
                        Forum
                      </Link>
                      <Link
                        to="/experten-bereich/ressourcen"
                        className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <BookOpen className="w-4 h-4" />
                        Ressourcen
                      </Link>
                      <Link
                        to="/experten-bereich/webinare"
                        className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Video className="w-4 h-4" />
                        Webinare
                      </Link>
                      <Link
                        to="/experten-bereich"
                        className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Award className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <BedarfsAnalysatorDialog
        open={showBedarfsAnalysator}
        onOpenChange={setShowBedarfsAnalysator}
      />
    </header>
  );
};

export default Header;
